// Agent 域名防墙(ewS3j + Ye71I)— 真 backend hook 集合
//
// backend 实装见 server/internal/httpapi/agent_jumpdomain_handlers.go(Phase B2.6.B)
// API 协议见 docs/api-list/agent/jumpdomain.md
//
// 8 hook:
//   ewS3j(DNS credentials):
//     useDnsCredentials / useSetDnsCredential / useTestDnsCredential
//   Ye71I(jump chain + domain):
//     useJumpChains / useCreateJumpChain / useCreateJumpDomain / useRefreshDomainDNS

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, ApiClientError } from '@dogdisk/shared'

// ============================================================================
// DNS Credentials(ewS3j)
// ============================================================================

export type CredentialStatusRow = {
  id: string
  provider: 'cloudflare' | 'godaddy' | string
  label: string
  masked_token: string
  account_id: string
  env: string
  status: string
  last_used_at: string | null
  configured_at: string
}

export type CredentialsResponse = {
  credentials: CredentialStatusRow[]
}

export function useDnsCredentials() {
  return useQuery({
    queryKey: ['agent', 'jumpdomain', 'credentials'] as const,
    queryFn: () =>
      apiClient.get<CredentialsResponse>('/gapi/agent/dns-credential'),
    staleTime: 60_000,
  })
}

export type SetCredentialInput = {
  provider: 'cloudflare' | 'godaddy'
  label?: string
  api_key: string
  api_secret?: string
  account_id?: string
  env?: 'production' | 'sandbox'
}

export function useSetDnsCredential() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SetCredentialInput) =>
      apiClient.put<{ status: string }>('/gapi/agent/dns-credential', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent', 'jumpdomain', 'credentials'] })
    },
  })
}

export type TestCredentialResult = {
  ok: boolean
  provider: string
  zones_count: number
  error?: string
}

export function useTestDnsCredential() {
  return useMutation({
    mutationFn: (credentialID: string) =>
      apiClient.post<TestCredentialResult>('/gapi/agent/dns-credential/test', {
        credential_id: credentialID,
      }),
  })
}

// ============================================================================
// Jump Chains + Domains(Ye71I)
// ============================================================================

export type DomainHealth = 'up' | 'down' | 'unknown'

export type JumpDomainRow = {
  id: string
  chain_id: string
  level: 1 | 2 | 3
  domain: string
  credential_id: string | null
  dns_synced_at: string | null
  dns_sync_status: 'synced' | 'pending' | 'failed' | null
  dns_sync_error: string | null
  status: 'active' | 'disabled' | 'rotated_out'
  // 探活结果(hourly cron + 「一键测试」写入);omitempty → 旧数据/未探活时可能为 undefined。
  health?: DomainHealth
  last_checked_at?: string | null
  // 系统写、agent 只读:自动禁用原因 / L1L2 超阈告警(真人测速 cron 写入);仅展示不可编辑。
  notes?: string | null
  created_at: string
}

export type ChainLandingType = 'site_agent' | 'custom'

export type JumpChainWithDomains = {
  id: string
  agent_id: string
  name: string
  status: 'active' | 'disabled'
  landing_type: ChainLandingType
  sort_order: number
  created_at: string
  domains: JumpDomainRow[]
}

// backend ListJumpChains 已 mount(GET /gapi/agent/jump-chains);404 分支保留作防御性退化
export function useJumpChains() {
  return useQuery({
    queryKey: ['agent', 'jumpdomain', 'chains'] as const,
    queryFn: async () => {
      try {
        return await apiClient.get<{ chains: JumpChainWithDomains[] }>(
          '/gapi/agent/jump-chains',
        )
      } catch (e) {
        // backend 未 mount → 退化空 list,UI 显空状态
        if (e instanceof ApiClientError && e.status === 404) {
          return { chains: [] as JumpChainWithDomains[] }
        }
        throw e
      }
    },
    staleTime: 60_000,
  })
}

export type CreateChainInput = { name: string; sort_order?: number }

export function useCreateJumpChain() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateChainInput) =>
      apiClient.post<JumpChainWithDomains>('/gapi/agent/jump-chains', input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['agent', 'jumpdomain', 'chains'] }),
  })
}

export type UpdateChainInput = {
  id: string
  name?: string
  status?: 'active' | 'disabled'
  sort_order?: number
  landing_type?: ChainLandingType
}

// PATCH /gapi/agent/jump-chains/{id} — 改 name / status / sort_order / landing_type(本期主要用 landing_type)。
export function useUpdateChain() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateChainInput) =>
      apiClient.patch<JumpChainWithDomains>(`/gapi/agent/jump-chains/${id}`, patch),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['agent', 'jumpdomain', 'chains'] }),
  })
}

export type ChainHealthCheckResult = {
  ok: boolean
  checked: number
  up: number
  down: number
}

// POST /gapi/agent/jump-chains/{id}/health-check — 「一键测试」同步探活整组 L1/L2 域名,
// 回写 health;成功后 invalidate chains 触发 refetch 刷新行内 health 徽章。
export function useHealthCheckChain() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (chainID: string) =>
      apiClient.post<ChainHealthCheckResult>(
        `/gapi/agent/jump-chains/${chainID}/health-check`,
        {},
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['agent', 'jumpdomain', 'chains'] }),
  })
}

export type CreateDomainInput = {
  chain_id: string
  level: 1 | 2 | 3
  domain: string
  credential_id?: string
}

export function useCreateJumpDomain() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateDomainInput) =>
      apiClient.post<JumpDomainRow>('/gapi/agent/jump-domains', input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['agent', 'jumpdomain', 'chains'] }),
  })
}

export type RefreshDomainResult = { ok: boolean; job_id?: string; error?: string }

export function useRefreshDomainDNS() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ domain_id, target_ip }: { domain_id: string; target_ip: string }) =>
      apiClient.post<RefreshDomainResult>(
        `/gapi/agent/jump-domains/${domain_id}/refresh-dns`,
        { target_ip },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['agent', 'jumpdomain', 'chains'] }),
  })
}

export function useDeleteChain() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.del<{ ok: boolean }>(`/gapi/agent/jump-chains/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['agent', 'jumpdomain', 'chains'] }),
  })
}

export function useDeleteDomain() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.del<{ ok: boolean }>(`/gapi/agent/jump-domains/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['agent', 'jumpdomain', 'chains'] }),
  })
}

export type UpdateDomainInput = {
  id: string
  credential_id?: string | null // '' / null → 解绑;省略 → 不动
  status?: 'active' | 'disabled'
}

export function useUpdateDomain() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateDomainInput) =>
      apiClient.patch<JumpDomainRow>(`/gapi/agent/jump-domains/${id}`, patch),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['agent', 'jumpdomain', 'chains'] }),
  })
}
